import { create } from 'zustand'
import { errorMessage } from '../../api/client'
import type { AttachmentKind } from '../../api/types'
import { attachmentKind, checkFile, isImageType, MAX_ATTACHMENTS } from '../../lib/files'
import { lengthProblem } from '../../lib/media'
import { toast } from '../../state/toasts'
import { readMediaDuration } from '../../upload/mediaLength'
import { prepareUpload, uploadAttachment } from '../../upload/upload'

export type UploadStatus = 'preparing' | 'uploading' | 'done' | 'error'

export interface PendingUpload {
  localId: string
  channelId: string
  file: File
  fileName: string
  sizeBytes: number
  contentType: string
  kind: AttachmentKind
  /** Video and audio: how long it plays, once read; null until then, or when the browser can't tell. */
  durationMs: number | null
  previewUrl: string | null
  progress: number
  status: UploadStatus
  error: string | null
  attachmentId: string | null
  width: number | null
  height: number | null
}

interface UploadsState {
  byChannel: Record<string, PendingUpload[]>
  patch: (channelId: string, localId: string, patch: Partial<PendingUpload>) => void
  add: (upload: PendingUpload) => void
  remove: (channelId: string, localId: string) => void
  clear: (channelId: string) => void
}

const controllers = new Map<string, AbortController>()
let counter = 0

export const useUploads = create<UploadsState>((set, get) => ({
  byChannel: {},
  patch: (channelId, localId, patch) =>
    set({
      byChannel: {
        ...get().byChannel,
        [channelId]: (get().byChannel[channelId] ?? []).map((u) => (u.localId === localId ? { ...u, ...patch } : u)),
      },
    }),
  add: (upload) =>
    set({ byChannel: { ...get().byChannel, [upload.channelId]: [...(get().byChannel[upload.channelId] ?? []), upload] } }),
  remove: (channelId, localId) => {
    const list = get().byChannel[channelId] ?? []
    const target = list.find((u) => u.localId === localId)
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
    controllers.get(localId)?.abort()
    controllers.delete(localId)
    set({ byChannel: { ...get().byChannel, [channelId]: list.filter((u) => u.localId !== localId) } })
  },
  clear: (channelId) => {
    for (const u of get().byChannel[channelId] ?? []) {
      if (u.previewUrl) URL.revokeObjectURL(u.previewUrl)
      controllers.delete(u.localId)
    }
    set({ byChannel: { ...get().byChannel, [channelId]: [] } })
  },
}))

async function runUpload(upload: PendingUpload): Promise<void> {
  const { patch, remove } = useUploads.getState()
  const controller = new AbortController()
  const { signal } = controller
  controllers.set(upload.localId, controller)
  try {
    patch(upload.channelId, upload.localId, { status: 'preparing', error: null, progress: 0 })
    let durationMs = upload.durationMs
    if ((upload.kind === 'video' || upload.kind === 'audio') && durationMs === null) {
      // Checked before uploading (contract: "Attachments", length limits); the server checks only the size.
      durationMs = await readMediaDuration(upload.file, upload.kind, { signal })
      if (signal.aborted) return
      const problem = lengthProblem(upload.fileName, upload.kind, durationMs)
      if (problem) {
        remove(upload.channelId, upload.localId)
        toast(problem)
        return
      }
      patch(upload.channelId, upload.localId, { durationMs })
    }
    const prepared = await prepareUpload(upload.file, upload.contentType)
    if (signal.aborted) return
    const check = checkFile(prepared.file.name, prepared.contentType, prepared.file.size)
    if (!check.ok) throw new Error(check.reason)
    patch(upload.channelId, upload.localId, { status: 'uploading', fileName: prepared.file.name, sizeBytes: prepared.file.size })
    const result = await uploadAttachment(
      upload.channelId,
      prepared,
      (progress) => patch(upload.channelId, upload.localId, { progress }),
      signal,
      { durationMs: durationMs ?? undefined },
    )
    patch(upload.channelId, upload.localId, {
      status: 'done',
      progress: 1,
      attachmentId: result.attachmentId,
      contentType: result.contentType,
      width: result.width,
      height: result.height,
    })
  } catch (error) {
    if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return
    patch(upload.channelId, upload.localId, { status: 'error', error: errorMessage(error, 'Upload failed') })
  } finally {
    if (controllers.get(upload.localId) === controller) controllers.delete(upload.localId)
  }
}

/** Validate files (type, size, count) and start uploading the accepted ones; videos and audio are checked for length first. */
export function addFiles(channelId: string, files: File[]): void {
  const existing = useUploads.getState().byChannel[channelId]?.length ?? 0
  let room = MAX_ATTACHMENTS - existing
  for (const file of files) {
    if (room <= 0) {
      toast(`You can attach up to ${MAX_ATTACHMENTS} files per message.`)
      break
    }
    // Images are checked again after downscaling, so allow large originals through to that step.
    const reported = checkFile(file.name, file.type, 1)
    if (!reported.ok) {
      toast(reported.reason)
      continue
    }
    if (!isImageType(reported.contentType)) {
      const sized = checkFile(file.name, file.type, file.size)
      if (!sized.ok) {
        toast(sized.reason)
        continue
      }
    }
    room--
    const upload: PendingUpload = {
      localId: `upload-${Date.now()}-${counter++}`,
      channelId,
      file,
      fileName: file.name,
      sizeBytes: file.size,
      contentType: reported.contentType,
      kind: attachmentKind(reported.contentType),
      durationMs: null,
      previewUrl: isImageType(reported.contentType) && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null,
      progress: 0,
      status: 'preparing',
      error: null,
      attachmentId: null,
      width: null,
      height: null,
    }
    useUploads.getState().add(upload)
    void runUpload(upload)
  }
}

export function retryUpload(upload: PendingUpload): void {
  void runUpload(upload)
}
